import { ColumnConfig } from '@components/design/migrated/Table';
import { ColumnHeaderDisplayNameForCL, ColumnHeaderForCL } from '@models/CategoryLeaderboard';
import { ShareType } from '@utils/pageConfig/Types';
import { sortFields } from '@utils/TableUtils';
import { KpiDropdownOption } from '@data-visualization/performance-segment-chart/Types';
import { SortingOptions } from '@components/design/migrated/Table/Sorting/types';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';

export const getKPIs = (columnConfig: ColumnConfig[]): KpiDropdownOption[] =>
  columnConfig.map(({ headerKey, name }) => ({
    label: name,
    value: headerKey as ColumnHeaderForCL,
  }));

export const getCategoryLeaderboardColumns = (selectedShareType: ShareType, volume: string): ColumnConfig[] => {
  const commonColumnFields = {
    className: 'z-20',
    isSortable: true,
    sortFields,
    compact: true,
  };

  return [
    {
      headerKey: ColumnHeaderForCL.marketRevenue,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.marketRevenue],
    },
    {
      headerKey: ColumnHeaderForCL.marketExcludeRetailRevenue,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.marketExcludeRetailRevenue],
    },
    {
      headerKey: ColumnHeaderForCL.yourRevenue,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.yourRevenue],
    },
    {
      headerKey:
        selectedShareType === ShareType.MarketShare
          ? ColumnHeaderForCL.yourRevenueShare
          : ColumnHeaderForCL.yourRevenueMix,
      name:
        selectedShareType === ShareType.MarketShare
          ? ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.yourRevenueShare]
          : ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.yourRevenueMix],
    },
    {
      headerKey: ColumnHeaderForCL.marketVolume,
      name: ColumnHeaderDisplayNameForCL(volume)[ColumnHeaderForCL.marketVolume],
    },
    {
      headerKey: ColumnHeaderForCL.marketExcludeRetailVolume,
      name: ColumnHeaderDisplayNameForCL(volume)[ColumnHeaderForCL.marketExcludeRetailVolume],
    },
    {
      headerKey: ColumnHeaderForCL.yourVolume,
      name: ColumnHeaderDisplayNameForCL(volume)[ColumnHeaderForCL.yourVolume],
    },
    {
      headerKey:
        selectedShareType === ShareType.MarketShare
          ? ColumnHeaderForCL.yourVolumeShare
          : ColumnHeaderForCL.yourVolumeMix,
      name:
        selectedShareType === ShareType.MarketShare
          ? ColumnHeaderDisplayNameForCL(volume)[ColumnHeaderForCL.yourVolumeShare]
          : ColumnHeaderDisplayNameForCL(volume)[ColumnHeaderForCL.yourVolumeMix],
    },
    {
      headerKey: ColumnHeaderForCL.marketPrice,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.marketPrice],
    },
    {
      headerKey: ColumnHeaderForCL.marketExcludeRetailPrice,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.marketExcludeRetailPrice],
    },
    {
      headerKey: ColumnHeaderForCL.yourPrice,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.yourPrice],
    },
  ].map((column) => ({ ...commonColumnFields, ...column }));
};

export const getCategoryLeaderboardOptions = (volume: string): ColumnConfig[] => {
  const commonColumnFields = {
    className: 'z-20',
    isSortable: true,
    sortFields,
    compact: true,
  };

  return [
    {
      headerKey: ColumnHeaderForCL.marketRevenue,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.marketRevenue],
    },
    {
      headerKey: ColumnHeaderForCL.marketExcludeRetailRevenue,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.marketExcludeRetailRevenue],
    },
    {
      headerKey: ColumnHeaderForCL.marketVolume,
      name: ColumnHeaderDisplayNameForCL(volume)[ColumnHeaderForCL.marketVolume],
    },
    {
      headerKey: ColumnHeaderForCL.marketExcludeRetailVolume,
      name: ColumnHeaderDisplayNameForCL(volume)[ColumnHeaderForCL.marketExcludeRetailVolume],
    },
    {
      headerKey: ColumnHeaderForCL.yourRevenue,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.yourRevenue],
    },
    {
      headerKey: ColumnHeaderForCL.yourRevenueShare,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.yourRevenueShare],
    },
    {
      headerKey: ColumnHeaderForCL.yourRevenueMix,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.yourRevenueMix],
    },
    {
      headerKey: ColumnHeaderForCL.yourVolume,
      name: ColumnHeaderDisplayNameForCL(volume)[ColumnHeaderForCL.yourVolume],
    },
    {
      headerKey: ColumnHeaderForCL.yourVolumeShare,
      name: ColumnHeaderDisplayNameForCL(volume)[ColumnHeaderForCL.yourVolumeShare],
    },
    {
      headerKey: ColumnHeaderForCL.yourVolumeMix,
      name: ColumnHeaderDisplayNameForCL(volume)[ColumnHeaderForCL.yourVolumeMix],
    },
    {
      headerKey: ColumnHeaderForCL.marketPrice,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.marketPrice],
    },
    {
      headerKey: ColumnHeaderForCL.marketExcludeRetailPrice,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.marketExcludeRetailPrice],
    },
    {
      headerKey: ColumnHeaderForCL.yourPrice,
      name: ColumnHeaderDisplayNameForCL()[ColumnHeaderForCL.yourPrice],
    },
  ].map((column) => ({ ...commonColumnFields, ...column }));
};

export const updateShareTypeAndSortingKeyCL = (
  { sortingOptions, setSortingOptions }: PagesConfig,
  setLocalShareType: (newShareType: ShareType) => void,
) => {
  return (shareType: ShareType) => {
    setLocalShareType(shareType);

    const finalHeaderKey = getNextBestSortingKeyForShareOrMixHeaders(sortingOptions);

    if (sortingOptions.headerKey !== finalHeaderKey) {
      setSortingOptions({
        ...sortingOptions,
        headerKey: finalHeaderKey,
      });
    }
  };
};

export const getNextBestSortingKeyForShareOrMixHeaders = ({ headerKey }: SortingOptions) => {
  const isMix = headerKey.includes('Mix');
  const isShare = headerKey.includes('Share');

  if (!isMix && !isShare) {
    return headerKey;
  }

  const isRevenue = headerKey.includes('Revenue');
  if (isRevenue) {
    return isMix ? ColumnHeaderForCL.yourRevenueShare : ColumnHeaderForCL.yourRevenueMix;
  }

  return isMix ? ColumnHeaderForCL.yourVolumeShare : ColumnHeaderForCL.yourVolumeMix;
};
